import React from 'react'
import { connect } from 'react-redux'
import { useHistory } from 'react-router-dom'
import {
  CircularProgress,
  makeStyles,
  styled,
  Avatar,
  Tooltip,
  Backdrop,
  Input,
  Badge
} from '@material-ui/core'
import { AvatarGroup } from '@material-ui/lab'
import { Cached as ReloadIcon } from '@material-ui/icons'
import Button from '../components/button/Button'

import { getChildrenRSPA } from '../store/classrooms/actions'
import {
  getQueuesCounterRSPA,
  getPickupQueue,
  clearPickupQueues,
  editPickupQueueRSPA,
  deletePickupQueueRSPA
} from '../store/pickup_queue/actions'
import { showAlertMessage } from '../store/alert/actions'

import InputSelect from '../components/select/InputSelect'
import { extractDayRange } from '../utils/extractDate'
import ConfirmModal from '../components/modal/ConfirmModal'
import useQueueWebSocket from '../utils/useQueueWebSocket'
import CustomTable from '../components/table/CustomTable'
import UserAvatar from '../components/shared/UserAvatar'
import StyledSVG from '../components/shared/StyledSVG'
import { numberFromText } from '../utils/helpers'
import { colors } from '../utils/constants'

import createQueueSuccessSVG from '../assets/icons/success-pickup-backdrop.svg'
import forwardIcon from '../assets/icons/forward-icon.svg'
import searchIcon from '../assets/icons/search-icon.svg'
import warningIcon from '../assets/icons/triangle-warning-red.svg'
import clockIcon from '../assets/icons/clock-icon.svg'
import configGearIcon from '../assets/icons/config-gear-icon.svg'
import roundedAddIcon from '../assets/icons/rounded-add-cross-icon.svg'
import roundedGreenCheck from '../assets/icons/rounded-green-check.svg'
import trashIcon from '../assets/icons/trash.svg'

const useStyle = makeStyles(theme => ({
  root: {
    paddingTop: 36,
    fontFamily: 'Montserrat, Source Sans Pro',
    width: '96%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 72
  },
  title_section: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: 16,
    '& .title': {
      margin: 0,
      color: '#386093',
      fontWeight: 600,
      fontSize: 32
    },
    '& .subtitle': {
      margin: 0,
      fontWeight: 400,
      fontSize: 16
    }
  },
  info_banners: {
    display: 'grid',
    gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
    gap: 32
  },
  banners_section: {
    display: 'flex',
    flexDirection: 'column',
    gap: 32,
    '& h1.title': {
      color: '#386093',
      fontWeight: 600,
      fontSize: 28,
      margin: 0
    }
  },
  avatar_wrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    '& p': {
      fontWeight: 500,
      fontSize: 16,
      color: '#555555'
    }
  },
  without_photo_preview: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '17px',
    letterSpacing: '0em',
    textAlign: 'left'
  },
  finish_pickup_queue: {
    display: 'flex',
    alignItems: 'center',
    columnGap: 32,
    '& > div': {
      cursor: 'pointer'
    },
    '& .red-trash > path': {
      stroke: '#EF7C8F'
    }
  },
  backdrop_icon_wrapper: {
    width: 400,
    height: 320,
    borderRadius: 24,
    backgroundColor: '#386093',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: '32px 56px 48px 48px',
    rowGap: 16,
    '& .title': {
      margin: 0,
      color: '#F1F3F4',
      fontWeight: 600,
      fontSize: 24
    },
    '& .subtitle': {
      margin: 0,
      color: '#FFFFFF',
      fontWeight: 400,
      fontSize: 14
    }
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1
  },
  queue_description: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: 16,
    marginBottom: 32,
    '& .title': {
      margin: 0,
      color: '#386093',
      fontSize: 28,
      fontWeight: 600
    },
    '& .subtitle': {
      margin: 0,
      color: '#555555',
      fontSize: 16,
      fontWeight: 400
    }
  },
  queues_filter_wrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  queues_filter: {
    display: 'flex',
    alignItems: 'flex-start',
    columnGap: 56
  },
  default_row_style: {
    backgroundColor: '#FFFFFF !important',
    '& td:last-child': {
      borderTopRightRadius: '16px',
      borderBottomRightRadius: '16px'
    },
    '& td:first-child': {
      borderTopLeftRadius: '16px',
      borderBottomLeftRadius: '16px'
    }
  },
  temporary_row_style: {
    backgroundColor: '#F8E7D6 !important',
    '& td:last-child': {
      borderTopRightRadius: '16px',
      borderBottomRightRadius: '16px'
    },
    '& td:first-child': {
      borderTopLeftRadius: '16px',
      borderBottomLeftRadius: '16px'
    }
  },
  queue_table: {
    backgroundColor: 'unset',
    '& .MuiTable-root': {
      borderCollapse: 'separate !important',
      borderSpacing: '0px 12px !important'
    }
  },
  pickup_card_today: {
    '& svg': {
      '& path': {
        stroke: '#C58561'
      }
    }
  },
  pickup_card_pending: {
    '& svg': {
      '& path': {
        stroke: '#FFFFFF'
      }
    }
  },
  pickup_manual_option: {
    '& svg': {
      '& path': {
        fill: '#555555'
      }
    }
  },
  pickup_config_option: {
    '& svg': {
      '& path': {
        stroke: '#555555'
      }
    }
  },
  input_select_wrapper: {
    width: 400
  },
  input_wrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: 250,
    gap: 8,
    alignItems: 'flex-start',
    '& .label': {
      fontSize: 12,
      fontWeight: 600,
      margin: 0
    }
  },
  search_component: {
    backgroundColor: '#FFFFFF',
    padding: '0px 8px',
    width: '100%',
    border: '1px solid #D2D5D8',
    minHeight: 40,
    borderRadius: 8
  },
  reload_btn_wrapper: {
    width: 250,
    '& .MuiButtonBase-root': {
      width: '100%'
    }
  },
  reload_btn: {
    backgroundColor: '#386093',
    height: 40,
    padding: '14px 48px',
    borderRadius: 8,
    '&:hover': {
      backgroundColor: '#386093'
    },
    fontSize: '16px',
    fontWeight: '600 !important',
    textTransform: 'none !important',
    lineHeight: '19.5px',
    '&.Mui-disabled': {
      pointerEvents: 'auto',
      cursor: 'pointer'
    }
  },
  reload_btn_label: {
    display: 'flex',
    alignItems: 'center',
    columnGap: 8,
    '& p': {
      fontWeight: 600,
      fontSize: 16,
      margin: 0
    }
  },
  queues_notification_badge: {
    '& .MuiBadge-badge': {
      width: 32,
      height: 32,
      borderRadius: 64,
      backgroundColor: '#F4A87C',
      color: '#FFFFFF',
      fontWeight: 600,
      fontSize: 14
    }
  }
}))

const EstimatedArrival = styled('div')(({ theme, bgColor, color }) => ({
  backgroundColor: bgColor || 'unset',
  color: color,
  width: 'fit-content',
  padding: '4px 8px',
  height: 30,
  borderRadius: 4,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& p': {
    margin: 0,
    fontSize: 16,
    fontWeight: 500
  }
}))

const PickupCard = styled('div')(({ theme, bgColor, textColor }) => ({
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  padding: 32,
  borderRadius: 16,
  backgroundColor: bgColor,
  color: textColor,
  gridColumn: 'span 4',
  height: 240,
  gap: 20,
  '& .banner_title': {
    fontSize: 28,
    margin: 0
  },
  '& .banner_data': {
    display: 'flex',
    gap: 8,
    alignItems: 'flex-end',
    '& p': {
      margin: 0
    },
    '& .value': {
      fontSize: 48,
      fontWeight: 600,
      lineHeight: '42px'
    },
    '& .label': {
      fontSize: 16,
      fontWeight: 500
    }
  }
}))

const PickupOption = styled('div')(({ bgColor }) => ({
  padding: '32px 48px',
  display: 'flex',
  gridColumn: 'span 6',
  gap: 16,
  backgroundColor: bgColor,
  borderRadius: 16,
  height: 130,
  alignItems: 'center',
  cursor: 'pointer',
  '& .labels': {
    display: 'flex',
    flexDirection: 'column',
    color: '#555555',
    gap: 8,
    '& h1': {
      fontWeight: 600,
      fontSize: 28,
      margin: 0
    },
    '& p': {
      fontWeight: 500,
      fontSize: 16,
      margin: 0
    }
  }
}))

const CounterValue = ({ value, isFetching }) => {
  if (isFetching) {
    return <CircularProgress />
  }
  return <p className='value'>{value}</p>
}

const PickupPage = ({
  getQueuesCounterRSPA,
  queues,
  clearPickupQueues,
  editPickupQueueRSPA,
  deletePickupQueueRSPA,
  getPickupQueue,
  schoolId,
  currentUser,
  alert,
  getChildrenRSPA
}) => {
  const classes = useStyle()
  const history = useHistory()
  const today = new Date()
  const { beginningOfDay, endOfDay } = extractDayRange(today)
  const [backdrop, setBackdrop] = React.useState({ open: false, svg: null })
  const [confirmModal, setConfirmModal] = React.useState({
    open: false,
    parent: null,
    child: null,
    id: null
  })
  const {
    error,
    queuesNotifications,
    setQueuesNotifications
  } = useQueueWebSocket({
    fractalId: currentUser.attributes.fractal_id,
    schoolId
  })
  const defaultParams = {
    'q[s]': 'estimated_arrival asc',
    'q[updated_at_gteq]': beginningOfDay,
    'q[updated_at_lteq]': endOfDay,
    'q[status_eq]': 0,
    include: 'approved_by,pickup_authorization.user,pickup_queue_children.child'
  }
  const [queusCounter, setQueuesCounter] = React.useState({
    isFetching: true,
    data: {}
  })
  const authorizationType = {
    permanent: 'Permanente',
    temporary: 'Temporário'
  }
  const [selectedChild, setSelectedChild] = React.useState([])
  const [parentName, setParentName] = React.useState(null)
  const [vehiclePlate, setVehiclePlate] = React.useState(null)

  const columns = [
    {
      key: 'parent',
      name: 'Responsável',
      width: '25%',
      render: row => {
        const parentName =
          row?.pickup_authorization?.user?.name ||
          row?.pickup_authorization?.name
        const parentPhoto =
          row?.pickup_authorization?.user?.avatar_url ||
          row?.pickup_authorization?.photo_url
        return (
          <div className={classes.avatar_wrapper}>
            <UserAvatar
              avatarStyle={{
                width: 40,
                height: 40
              }}
              withoutUserName
              userName={parentName}
              avatarUrl={parentPhoto}
            />
            <p>{parentName}</p>
          </div>
        )
      }
    },
    {
      key: 'child',
      name: 'Aluno(s)',
      width: '40%',
      render: row => {
        return (
          <div className={classes.avatar_wrapper}>
            <AvatarGroup max={5} spacing={1}>
              {row?.pickup_queue_children?.map((pckQueueChild, idx) => {
                const index =
                  numberFromText(pckQueueChild?.child?.name?.slice(0, 2)) %
                  colors.length
                return (
                  <Tooltip
                    key={`child[${idx}]`}
                    title={pckQueueChild?.child?.name}
                    arial-label='avatar-usuario'
                  >
                    <Avatar
                      style={{
                        height: '30px',
                        width: '30px',
                        backgroundColor:
                          // eslint-disable-next-line standard/computed-property-even-spacing
                          colors[index]
                      }}
                      src={pckQueueChild?.child?.avatar_url}
                    >
                      <p className={classes.without_photo_preview}>
                        {pckQueueChild?.child?.name[0]}
                      </p>
                    </Avatar>
                  </Tooltip>
                )
              })}
            </AvatarGroup>
            <p>{row?.pickup_queue_children?.[0]?.child?.name}</p>
          </div>
        )
      }
    },
    {
      key: 'type',
      name: 'Tipo',
      width: '10%',
      render: row => {
        const isParent = !row.authorization_id && row.user_id
        return (
          <div>
            <p>
              {isParent
                ? 'Responsável autorizado'
                : authorizationType[row?.pickup_authorization?.pickup_type]}
            </p>
          </div>
        )
      }
    },
    {
      key: 'vehicle_plate',
      name: 'Placa',
      width: '10%',
      render: row => {
        return (
          <p>
            {row?.pickup_authorization?.vehicle_plate
              ? row?.pickup_authorization?.vehicle_plate?.toUpperCase()
              : 'Pedestre'}
          </p>
        )
      }
    },
    {
      key: 'estimated_arrival',
      name: 'Estimativa',
      width: '10%',
      render: row => {
        const now = new Date()
        const estimated_arrival = new Date(row.estimated_arrival)
        const diffMs = estimated_arrival - now
        const diffMinutes = Math.round(diffMs / 1000 / 60)
        return (
          <EstimatedArrival
            color={diffMinutes < 5 ? '#FFFFFF' : '#555555'}
            bgColor={diffMinutes < 5 ? '#EF7C8F' : '#D9E8FA'}
          >
            <p>{`${diffMinutes > 0 ? diffMinutes : 0} minutos`}</p>
          </EstimatedArrival>
        )
      }
    },
    {
      key: 'finish_pickup_queue',
      name: '',
      // width: '5%',
      render: row => {
        return (
          <div className={classes.finish_pickup_queue}>
            <Tooltip
              title={'Concluir retirada'}
              arial-label='concluir-retirada'
            >
              <div onClick={() => handleConfirmFinishQueue(row.id)}>
                <StyledSVG src={roundedGreenCheck} width={28} height={28} />
              </div>
            </Tooltip>
            <Tooltip title={'Excluir retirada'} arial-label='excluir-retirada'>
              <div onClick={() => handleConfirmModal(row)}>
                <StyledSVG
                  className='red-trash'
                  src={trashIcon}
                  width={28}
                  height={28}
                />
              </div>
            </Tooltip>
          </div>
        )
      }
    }
  ]

  React.useEffect(() => {
    getQueuesCounterRSPA().then(res => {
      setQueuesCounter({
        isFetching: false,
        data: res.data
      })
    })
    getPickupQueue({
      page: 1,
      params: {
        ...defaultParams
      }
    })
    return () => {
      clearPickupQueues()
    }
  }, [])

  React.useEffect(
    () => {
      if (error) {
        alert('Erro ao conectar à fila.', 'error')
      }
    },
    [error]
  )

  const handleDeletePickupQueue = id => {
    deletePickupQueueRSPA(id)
      .then(res => {
        getPickupQueue({
          page: 1,
          params: {
            ...defaultParams
          }
        })
      })
      .catch(error => {
        console.error(error)
        alert('Erro ao excluir registro de retirada.', 'error')
      })
  }

  const handleConfirmModal = queue => {
    const reponsibleName =
      queue?.pickup_authorization?.name || queue?.user?.name
    setConfirmModal(prev => ({
      ...prev,
      open: true,
      id: queue.id,
      parent: reponsibleName,
      child: queue?.pickup_queue_children
        ?.map(item => item?.child?.name)
        ?.join(', ')
    }))
  }

  const handleConfirmFinishQueue = async id => {
    const newValues = {
      data: {
        type: 'pickup_queues',
        id,
        attributes: {
          status: 'finished'
        }
      }
    }
    setBackdrop(prev => ({ ...prev, open: true }))
    await editPickupQueueRSPA({ id, data: newValues })
      .then(res => {
        setBackdrop(prev => ({ ...prev, svg: createQueueSuccessSVG }))
        getPickupQueue({
          page: 1,
          params: {
            ...defaultParams
          }
        })
      })
      .catch(err => {
        console.error(err)
        setBackdrop({ svg: null, open: false })
        alert('Erro ao concluir a retirada.', 'error')
      })
  }

  const applyStyleByRowAttribute = row => {
    if (row?.pickup_authorization?.pickup_type === 'temporary') {
      return classes.temporary_row_style
    }
    return classes.default_row_style
  }

  const handleChangeParentName = event => {
    setParentName(event.target.value)
  }

  const handleChangeVehiclePlate = event => {
    setVehiclePlate(event.target.value)
  }

  const handleSearchQueue = () => {
    getPickupQueue({
      page: 1,
      params: {
        ...defaultParams,
        ...(selectedChild?.[0]
          ? { 'q[pickup_queue_children_id_eq]': selectedChild?.[0] }
          : {}),
        ...(parentName?.length > 0
          ? { 'q[pickup_authorization_name_cont]': parentName }
          : {}),
        ...(vehiclePlate?.length > 0
          ? { 'q[pickup_authorization_vehicle_plate_cont]': vehiclePlate }
          : {})
      }
    })
  }

  const reloadQueueTable = () => {
    getPickupQueue({
      page: 1,
      params: {
        ...defaultParams
      }
    })
    setQueuesNotifications([])
  }

  return (
    <div className={classes.root}>
      <div className={classes.title_section}>
        <h1 className='title'>Painel de saídas e autorizações</h1>
        <p className='subtitle'>
          Acompanhe o moveimento atual e gerencia as permissões de retirada.
        </p>
      </div>
      <div className={classes.banners_section}>
        <h1 className='title'>Administração</h1>
        <div className={classes.info_banners}>
          <PickupCard
            onClick={() => history.push('/pickup/history')}
            bgColor='#E2EFFF'
            textColor='#386093'
          >
            <StyledSVG src={forwardIcon} height={40} width={40} />
            <h1 className='banner_title'>Histórico de saídas</h1>
            <div className='banner_data'>
              <CounterValue
                isFetching={queusCounter.isFetching}
                value={queusCounter.data?.all_queues}
              />
              <p className='label'>Histórico geral</p>
            </div>
          </PickupCard>
          <PickupCard
            onClick={() => history.push('/pickup/today')}
            bgColor='#FFEFE2'
            textColor='#C58561'
            className={classes.pickup_card_today}
          >
            <StyledSVG src={clockIcon} height={40} width={40} />
            <h1 className='banner_title'>Saídas hoje</h1>
            <div className='banner_data'>
              <CounterValue
                isFetching={queusCounter.isFetching}
                value={queusCounter.data?.today_queues}
              />
              <p className='label'>Movimento diário</p>
            </div>
          </PickupCard>
          <PickupCard
            onClick={() => history.push('/pickup/pending')}
            bgColor='#EF7C8F'
            textColor='#FFFFFF'
            className={classes.pickup_card_pending}
          >
            <StyledSVG src={warningIcon} height={40} width={40} />
            <h1 className='banner_title'>Autorizações Pendentes</h1>
            <div className='banner_data'>
              <CounterValue
                isFetching={queusCounter.isFetching}
                value={queusCounter.data?.pending_authorizations}
              />
              <p className='label'>Solicitações para análise</p>
            </div>
          </PickupCard>
          <PickupOption
            onClick={() => history.push('/pickup/manual')}
            bgColor='#FFE2E2'
            className={classes.pickup_manual_option}
          >
            <StyledSVG src={roundedAddIcon} width={56} height={56} />
            <div className='labels'>
              <h1>Retirada manual</h1>
              <p>Registre manualmente uma retirada</p>
            </div>
          </PickupOption>
          <PickupOption
            onClick={() => history.push('/pickup/authorizations')}
            bgColor='#E3E3E3'
            className={classes.pickup_config_option}
          >
            <StyledSVG src={configGearIcon} width={56} height={56} />
            <div className='labels'>
              <h1>Gerenciar permissão</h1>
              <p>Edite e gerencie reponsáveis já cadastrados</p>
            </div>
          </PickupOption>
        </div>
      </div>
      <div className={classes.queues_section}>
        <div className={classes.queue_description}>
          <h1 className='title'>Fila de retirada do dia</h1>
          <p className='subtitle'>
            Acompanhe as retiradas previstas para hoje e atue quando necessário
          </p>
        </div>
        <div className={classes.queues_filter_wrapper}>
          <div className={classes.queues_filter}>
            <InputSelect
              bgColor={'#FFFFFF'}
              selectedItems={selectedChild}
              setSelectedItems={setSelectedChild}
              wrapperClass={classes.input_select_wrapper}
              fetchItems={getChildrenRSPA}
              placeholder={'Digite o nome do aluno'}
              label={'Aluno'}
              params={{
                'page[size]': 7
              }}
            />
            <div className={classes.input_wrapper}>
              <p className='label'>{'Responsável'}</p>
              <Input
                disableUnderline
                value={parentName}
                className={classes.search_component}
                placeholder='Busque pelo nome'
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    handleSearchQueue()
                  }
                }}
                onChange={e => handleChangeParentName(e)}
                endAdornment={
                  <StyledSVG
                    style={{ marginRight: 4 }}
                    src={searchIcon}
                    width={18}
                    height={18}
                  />
                }
              />
            </div>
            <div className={classes.input_wrapper}>
              <p className='label'>{'Placa'}</p>
              <Input
                disableUnderline
                value={vehiclePlate}
                className={classes.search_component}
                placeholder='Filtre pela placa'
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    handleSearchQueue()
                  }
                }}
                onChange={e => handleChangeVehiclePlate(e)}
                endAdornment={
                  <StyledSVG
                    style={{ marginRight: 4 }}
                    src={searchIcon}
                    width={18}
                    height={18}
                  />
                }
              />
            </div>
          </div>
          <Badge
            className={classes.queues_notification_badge}
            invisible={queuesNotifications.length === 0}
            badgeContent={`+ ${queuesNotifications.length}`}
          >
            <Button
              rootClass={classes.reload_btn_wrapper}
              className={classes.reload_btn}
              onClick={() => {
                reloadQueueTable()
              }}
            >
              <div className={classes.reload_btn_label}>
                <ReloadIcon />
                <p>Atualizar fila</p>
              </div>
            </Button>
          </Badge>
        </div>
        <CustomTable
          columns={columns}
          from={'pickup_queue'}
          data={queues.items}
          classRoot={classes.queue_table}
          applyStyleByRowAttribute={applyStyleByRowAttribute}
          params={{
            ...defaultParams
          }}
          fetchItems={getPickupQueue}
          isFetching={queues.isFetching}
          pagination={queues.pagination}
          border={'none'}
          headCellRadius
          headColor={'rgba(173, 184, 204, 0.3)'}
        />
        <Backdrop
          onClick={() => setBackdrop(prev => ({ svg: null, open: false }))}
          className={classes.backdrop}
          open={backdrop.open}
        >
          {backdrop.svg ? (
            <div className={classes.backdrop_icon_wrapper}>
              <StyledSVG width={120} height={110} src={createQueueSuccessSVG} />
              <h1 className='title'>Retirada Concluída</h1>
              <p className='subtitle'>
                Retirada concluída com sucesso. Tenha um ótimo dia!
              </p>
            </div>
          ) : (
            <CircularProgress />
          )}
        </Backdrop>
        <ConfirmModal
          title={`Deseja excluir a retirada de ${
            confirmModal?.child
          } destinada à ${confirmModal?.parent} ?`}
          open={confirmModal.open}
          setOpen={value => {
            if (value) {
              setConfirmModal({ ...confirmModal, open: value })
            } else {
              setConfirmModal({
                id: null,
                open: value,
                child: null,
                parent: null
              })
            }
          }}
          onConfirm={() => {
            handleDeletePickupQueue(confirmModal.id)
          }}
        />
      </div>
    </div>
  )
}

const mapStateToProps = (state, props) => {
  return {
    queues: state.pickup_queues,
    schoolId: state.school?.currentSchool?.school?.id,
    currentUser: state.auth.user.data
  }
}
const mapDispatchToProps = dispatch => ({
  getQueuesCounterRSPA: data => dispatch(getQueuesCounterRSPA.request(data)),
  getPickupQueue: data => dispatch(getPickupQueue(data)),
  clearPickupQueues: () => dispatch(clearPickupQueues()),
  editPickupQueueRSPA: data => dispatch(editPickupQueueRSPA.request(data)),
  deletePickupQueueRSPA: data => dispatch(deletePickupQueueRSPA.request(data)),
  getChildrenRSPA: data => dispatch(getChildrenRSPA.request(data)),
  alert: (message, type) =>
    dispatch(
      showAlertMessage({
        message: message,
        severity: type
      })
    )
})

export default connect(mapStateToProps, mapDispatchToProps)(PickupPage)
